import React, {useEffect, useState} from 'react';
import clsx from 'clsx';
import {useStore} from '@app/state/store';
import {loadFonts} from '@common/ui/font-picker/load-fonts';
import {FontFaceConfig} from '@common/ui/font-picker/font-face-config';
import {state, tools} from '@app/state/utils';
import {assetUrl} from '@app/utils/asset-url';
import {KeyboardArrowDownIcon} from '@common/icons/material/KeyboardArrowDown';
import {SearchIcon} from '@common/icons/material/Search';
import {AddIcon} from '@common/icons/material/Add';
import {FontDownloadIcon} from '@common/icons/material/FontDownload';
import {TextField} from '@common/ui/forms/input-field/text-field/text-field';
import {Trans} from '@common/i18n/trans';
import {openDialog, closeDialog} from '@common/ui/overlays/store/dialog-store';
import {DialogHeader} from '@common/ui/overlays/dialog/dialog-header';
import {DialogBody} from '@common/ui/overlays/dialog/dialog-body';
import {Dialog} from '@common/ui/overlays/dialog/dialog';
import {ColorPickerButton} from '../../../ui/color-picker-button';
import {ScrollableView, ScrollableViewItem} from '../../../ui/navbar/scrollable-view';
import {IconButton} from '@common/ui/buttons/icon-button';
import {ButtonBase} from '@common/ui/buttons/button-base';

export function TextNav() {
  const fonts = useStore(s => s.config.tools?.text?.items);
  const selectedFont = useStore(s => s.objects.active.editableProps.fontFamily);
  const textColor = useStore(s => s.objects.active.editableProps.fill);

  // load fonts into dom
  useEffect(() => {
    if (fonts) {
      loadFonts(fonts, {prefixSrc: assetUrl, id: 'pixpen-fonts'}).catch(
        () => {}
      );
    }
  }, [fonts]);

  // add text to canvas on text nav open
  useEffect(() => {
    const addedText = tools().text.selectOrAddText();
    if (addedText) {
      state().setDirty(true);
    }
  }, []);

  const selectedFontConfig = fonts?.find(f => f.family === selectedFont);

  const openFontModal = () => {
    openDialog(FontSelectionDialog, { fonts: fonts || [] });
  };

  const addNewText = () => {
    tools().text.add();
    state().setDirty(true);
  };

  return (
    <div className="pt-6">
      <div className="flex gap-10 items-center">
        <div className="flex-1">
          <FontSelectBox
            selectedFont={selectedFontConfig}
            onClick={openFontModal}
          />
        </div>
        <ColorPickerButton
          value={typeof textColor === 'string' ? textColor : '#000000'}
          onChange={(newColor) => {
            tools().objects.setValues({ fill: newColor });
            state().setDirty(true);
          }}
          aria-label="Text Color"
          size="sm"
        />
        <ButtonBase
          variant="flat"
          color="paper"
          className="flex-col flex-shrink-0 w-68 h-68"
          radius="none"
          onClick={addNewText}
        >
          <div className="mb-1">
            <AddIcon className="icon-md" />
          </div>
          <div className="mt-6 text-xs capitalize max-w-[90%] overflow-hidden overflow-ellipsis">
            <Trans message="New Text" />
          </div>
        </ButtonBase>
      </div>
    </div>
  );
}

type FontSelectBoxProps = {
  selectedFont?: FontFaceConfig;
  onClick: () => void;
};

function FontSelectBox({selectedFont, onClick}: FontSelectBoxProps) {
  return (
    <ButtonBase
      variant="flat"
      color="paper"
      className="flex-col flex-shrink-0 w-68 h-68"
      radius="none"
      onClick={onClick}
    >
      <div className="mb-1">
        <FontDownloadIcon className="icon-md" />
      </div>
      <div className="mt-6 text-xs capitalize max-w-[90%] overflow-hidden overflow-ellipsis">
        {selectedFont?.family || <Trans message="Select Font" />}
      </div>
    </ButtonBase>
  );
}

type FontSelectionDialogProps = {
  fonts: FontFaceConfig[];
};

function FontSelectionDialog({fonts}: FontSelectionDialogProps) {
  const selectedFont = useStore(s => s.objects.active.editableProps.fontFamily);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredFonts = fonts.filter((fontConfig: FontFaceConfig) =>
    fontConfig.family.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectFont = (fontConfig: FontFaceConfig) => {
    tools().text.selectOrAddText();
    state().setDirty(true);
    tools().objects.setValues({
      fontFamily: fontConfig.family,
    });
    closeDialog();
  };

  return (
    <Dialog size="md">
      <DialogHeader>
        <Trans message="Select Font" />
      </DialogHeader>
      <DialogBody>
        <div className="mb-4">
          <TextField
            placeholder="Search fonts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            startAdornment={<SearchIcon className="w-4 h-4 text-gray-400" />}
            size="sm"
          />
        </div>

        <div className="max-h-200 overflow-y-auto">
          {filteredFonts.map((fontConfig) => {
            const isSelected = selectedFont === fontConfig.family;
            return (
              <button
                key={fontConfig.family}
                type="button"
                className={clsx(
                  'w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 transition-colors',
                  {
                    'bg-primary-50 text-primary': isSelected,
                  }
                )}
                style={{
                  fontFamily: !fontConfig.family.includes('"')
                    ? `"${fontConfig.family}"`
                    : fontConfig.family,
                  fontWeight: fontConfig.descriptors?.weight || 'normal',
                }}
                onClick={() => selectFont(fontConfig)}
              >
                <div className="font-medium">{fontConfig.family}</div>
                <div className="text-sm text-gray-500 mt-1">The quick brown fox jumps</div>
              </button>
            );
          })}
          {filteredFonts.length === 0 && (
            <div className="p-4 text-center text-gray-500">
              <Trans message="No fonts found" />
            </div>
          )}
        </div>
      </DialogBody>
    </Dialog>
  );
}
